import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { 
  ArrowLeft, Copy, Check, Upload, ExternalLink, 
  FileImage, FileText, X, MessageCircle, Phone 
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_aestha-hub/artifacts/58pjy83g_IMG_0746.jpeg";

export default function Payment() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token } = useAuth();
  const fileInputRef = useRef(null);
  const subscriptionCreatedRef = useRef(false);
  
  const [paymentMethods, setPaymentMethods] = useState(null);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [subscriptionId, setSubscriptionId] = useState(null);
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [copied, setCopied] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const plan = location.state?.plan;
  const period = location.state?.period || "semiannual";

  useEffect(() => {
    if (!token) {
      navigate("/login");
      return;
    }
    if (!plan) {
      navigate("/select-plan");
      return;
    }
    fetchPaymentMethods();
    // Check if user already has a pending subscription request
    checkExistingSubscription();
  }, [token, plan, navigate]);

  const checkExistingSubscription = async () => {
    try {
      const statusResponse = await axios.get(`${API}/api/subscriptions/status`);
      if (statusResponse.data.latest_request) {
        setSubscriptionId(statusResponse.data.latest_request.id);
        subscriptionCreatedRef.current = true;
        // Pre-select the payment method from existing request
        if (statusResponse.data.latest_request.payment_method) {
          const existingMethod = paymentMethods?.methods?.find(
            m => m.id === statusResponse.data.latest_request.payment_method
          );
          if (existingMethod) {
            setSelectedMethod(existingMethod);
          }
        }
        if (statusResponse.data.latest_request.status === "proof_submitted") {
          setSubmitted(true);
        }
      }
    } catch (e) {
      // No existing subscription - that's fine
    }
  };

  const fetchPaymentMethods = async () => {
    try {
      const response = await axios.get(`${API}/api/payment-methods`);
      setPaymentMethods(response.data);
    } catch (error) {
      toast.error("Erro ao carregar métodos de pagamento");
    } finally {
      setLoading(false);
    }
  };

  const createSubscriptionRequest = async (paymentMethodId) => {
    // Guard against double-creation
    if (!plan || subscriptionId || subscriptionCreatedRef.current) return subscriptionId;
    subscriptionCreatedRef.current = true;
    
    try {
      const response = await axios.post(`${API}/api/subscriptions`, {
        plan: plan.id,
        period: period,
        payment_method: paymentMethodId
      });
      setSubscriptionId(response.data.id);
      return response.data.id;
    } catch (error) {
      subscriptionCreatedRef.current = false; // Reset on error to allow retry
      if (error.response?.data?.detail?.includes("pendente")) {
        // User already has a pending request, try to get it
        try {
          const statusResponse = await axios.get(`${API}/api/subscriptions/status`);
          if (statusResponse.data.latest_request) {
            setSubscriptionId(statusResponse.data.latest_request.id);
            subscriptionCreatedRef.current = true;
            if (statusResponse.data.latest_request.status === "proof_submitted") {
              setSubmitted(true);
            }
            return statusResponse.data.latest_request.id;
          }
        } catch (e) {
          console.error("Error getting subscription status", e);
        }
      }
      throw error;
    }
  };

  const copyToClipboard = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied({ ...copied, [field]: true });
      setTimeout(() => setCopied({ ...copied, [field]: false }), 2000);
      toast.success("Copiado!");
    } catch (error) {
      toast.error("Erro ao copiar");
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    // Validate file type
    const allowedTypes = ["image/jpeg", "image/png", "image/heic", "image/heif", "application/pdf"];
    if (!allowedTypes.includes(selectedFile.type)) {
      toast.error("Tipo de arquivo não permitido. Use: JPG, PNG, HEIC ou PDF.");
      return;
    }

    // Validate file size (10MB)
    if (selectedFile.size > 10 * 1024 * 1024) {
      toast.error("Arquivo muito grande. Máximo: 10MB.");
      return;
    }

    setFile(selectedFile);

    // Create preview for images
    if (selectedFile.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (e) => setFilePreview(e.target.result);
      reader.readAsDataURL(selectedFile);
    } else {
      setFilePreview(null);
    }
  };

  const handleSubmit = async () => {
    // Validate payment method selection
    if (!selectedMethod) {
      toast.error("Selecione um método de pagamento");
      return;
    }

    if (!file) {
      toast.error("Selecione o comprovante de pagamento");
      return;
    }

    setSubmitting(true);

    try {
      // Create subscription request if not exists
      let subId = subscriptionId;
      if (!subId) {
        subId = await createSubscriptionRequest(selectedMethod.id);
      }

      if (!subId) {
        toast.error("Erro ao processar solicitação. Tente novamente.");
        setSubmitting(false);
        return;
      }

      const formData = new FormData();
      formData.append("file", file);

      await axios.post(`${API}/api/subscriptions/${subId}/proof`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      setSubmitted(true);
      toast.success("Comprovante enviado com sucesso!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao enviar comprovante");
    } finally {
      setSubmitting(false);
    }
  };

  const getPlanLabel = (planId) => {
    switch(planId) {
      case "base": return "Base";
      case "advanced": return "Avançado";
      case "complete": return "Completo";
      default: return planId;
    }
  };

  const getPrice = () => {
    return period === "monthly" ? plan.monthly_price : plan.semiannual_price;
  };

  if (loading || !paymentMethods) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="loading-skeleton w-12 h-12 rounded-full"></div>
      </div>
    );
  }

  // Success state
  if (submitted) {
    return (
      <div className="min-h-screen bg-white" data-testid="payment-success">
        <header className="border-b py-4 px-6">
          <div className="max-w-3xl mx-auto">
            <img src={LOGO_URL} alt="AESTHA" className="h-8 invert" />
          </div>
        </header>

        <main className="max-w-lg mx-auto px-4 py-12 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check size={32} className="text-green-600" />
          </div>
          
          <h1 className="font-serif text-2xl mb-4">Pagamento enviado para análise!</h1>
          
          <p className="text-muted-foreground mb-8">
            Recebemos seu comprovante de pagamento! Seu pagamento será conferido e seu acesso 
            será liberado em até <strong>48 horas</strong> após o envio.
          </p>

          <Card className="p-6 text-left mb-8">
            <h3 className="font-mono text-xs uppercase tracking-widest mb-4">Resumo da Solicitação</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Plano:</span>
                <span className="font-medium">{getPlanLabel(plan.id)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Período:</span>
                <span className="font-medium">{period === "monthly" ? "Mensal" : "Semestral"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Valor:</span>
                <span className="font-medium">${getPrice().toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Status:</span>
                <span className="text-yellow-600 font-medium">Aguardando análise</span>
              </div>
            </div>
          </Card>

          <p className="text-sm text-muted-foreground mb-6">
            Você pode acompanhar o status da sua assinatura na sua conta. 
            Caso o prazo tenha passado e seu acesso ainda não tenha sido liberado, 
            entre em contato com nosso atendimento.
          </p>

          {/* Support Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center mb-8">
            <a 
              href={`${paymentMethods.support.whatsapp_link}?text=${encodeURIComponent(paymentMethods.support.message)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-none hover:bg-green-700 transition-colors"
            >
              <MessageCircle size={18} />
              WhatsApp
            </a>
            <a 
              href={`sms:${paymentMethods.support.whatsapp}`}
              className="inline-flex items-center justify-center gap-2 px-4 py-3 border text-foreground rounded-none hover:bg-secondary transition-colors"
            >
              <Phone size={18} />
              Mensagem de texto
            </a>
          </div>

          <Button 
            onClick={() => navigate("/dashboard")} 
            variant="outline" 
            className="btn-secondary"
          >
            Ir para minha conta
          </Button>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="payment-page">
      {/* Header */}
      <header className="border-b py-4 px-6">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <img src={LOGO_URL} alt="AESTHA" className="h-8 invert" />
          <Button 
            variant="ghost" 
            onClick={() => navigate("/select-plan")}
            className="text-muted-foreground"
          >
            <ArrowLeft size={16} className="mr-2" />
            Voltar
          </Button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8 lg:py-12">
        {/* Title */}
        <div className="text-center mb-8">
          <h1 className="font-serif text-2xl sm:text-3xl mb-2">
            Seu acesso à AESTHA está quase pronto!
          </h1>
          <p className="text-muted-foreground">
            Realize o pagamento e envie o comprovante para liberar seu acesso.
          </p>
        </div>

        {/* Order Summary */}
        <Card className="p-6 mb-8">
          <h3 className="font-mono text-xs uppercase tracking-widest mb-4">Resumo do Pedido</h3>
          <div className="flex justify-between items-center pb-4 border-b">
            <div>
              <p className="font-medium">Plano {getPlanLabel(plan.id)}</p>
              <p className="text-sm text-muted-foreground">
                {period === "monthly" ? "Mensal (30 dias)" : "Semestral (180 dias)"}
              </p>
            </div>
            <p className="font-serif text-2xl">${getPrice().toFixed(2)}</p>
          </div>
          <div className="flex justify-between items-center pt-4">
            <span className="font-medium">Total a pagar:</span>
            <span className="font-serif text-2xl">${getPrice().toFixed(2)}</span>
          </div>
        </Card>

        {/* Payment Methods */}
        <div className="mb-8">
          <h3 className="font-mono text-xs uppercase tracking-widest mb-4">Métodos de Pagamento</h3>
          
          <div className="space-y-4">
            {paymentMethods.methods.map((method) => (
              <Card 
                key={method.id}
                className={`p-4 cursor-pointer transition-all ${
                  selectedMethod?.id === method.id ? "ring-2 ring-black" : "hover:bg-secondary/30"
                }`}
                onClick={() => setSelectedMethod(method)}
                data-testid={`payment-method-${method.id}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-4 h-4 border-2 rounded-full ${
                      selectedMethod?.id === method.id ? "border-black bg-black" : "border-gray-300"
                    }`}>
                      {selectedMethod?.id === method.id && (
                        <div className="w-full h-full flex items-center justify-center">
                          <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                        </div>
                      )}
                    </div>
                    <span className="font-medium">{method.name}</span>
                  </div>
                  {method.link && (
                    <a 
                      href={method.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-muted-foreground hover:text-foreground"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <ExternalLink size={16} />
                    </a>
                  )}
                </div>

                {selectedMethod?.id === method.id && (
                  <div className="mt-4 pt-4 border-t space-y-3">
                    {method.number && (
                      <div className="flex items-center justify-between bg-secondary/50 p-3">
                        <div>
                          <p className="text-xs text-muted-foreground">Número</p>
                          <p className="font-mono">{method.number}</p>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); copyToClipboard(method.number, `${method.id}-number`); }}
                        >
                          {copied[`${method.id}-number`] ? <Check size={16} /> : <Copy size={16} />}
                        </Button>
                      </div>
                    )}
                    {method.recipient && (
                      <div className="flex items-center justify-between bg-secondary/50 p-3">
                        <div>
                          <p className="text-xs text-muted-foreground">Destinatário</p>
                          <p className="font-medium">{method.recipient}</p>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); copyToClipboard(method.recipient, `${method.id}-recipient`); }}
                        >
                          {copied[`${method.id}-recipient`] ? <Check size={16} /> : <Copy size={16} />}
                        </Button>
                      </div>
                    )}
                    {method.username && (
                      <div className="flex items-center justify-between bg-secondary/50 p-3">
                        <div>
                          <p className="text-xs text-muted-foreground">Usuário</p>
                          <p className="font-mono">{method.username}</p>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); copyToClipboard(method.username, `${method.id}-username`); }}
                        >
                          {copied[`${method.id}-username`] ? <Check size={16} /> : <Copy size={16} />}
                        </Button>
                      </div>
                    )}
                    {method.cashtag && (
                      <div className="flex items-center justify-between bg-secondary/50 p-3">
                        <div>
                          <p className="text-xs text-muted-foreground">Cashtag</p>
                          <p className="font-mono">{method.cashtag}</p>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); copyToClipboard(method.cashtag, `${method.id}-cashtag`); }}
                        >
                          {copied[`${method.id}-cashtag`] ? <Check size={16} /> : <Copy size={16} />}
                        </Button>
                      </div>
                    )}
                    <p className="text-sm text-muted-foreground">{method.instructions}</p>
                  </div>
                )}
              </Card>
            ))}
          </div>
        </div>

        {/* Upload Proof */}
        <div className="mb-8">
          <h3 className="font-mono text-xs uppercase tracking-widest mb-4">Comprovante de Pagamento</h3>
          
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept=".jpg,.jpeg,.png,.heic,.heif,.pdf"
            className="hidden"
            data-testid="file-input"
          />

          {!file ? (
            <div 
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed p-8 text-center cursor-pointer hover:bg-secondary/30 transition-colors"
              data-testid="upload-area"
            >
              <Upload size={32} className="mx-auto mb-3 text-muted-foreground" />
              <p className="font-medium mb-1">Clique para enviar o comprovante</p>
              <p className="text-sm text-muted-foreground">
                Formatos aceitos: JPG, PNG, HEIC, PDF (máx. 10MB)
              </p>
            </div>
          ) : (
            <Card className="p-4">
              <div className="flex items-center gap-4">
                {filePreview ? (
                  <img 
                    src={filePreview} 
                    alt="Preview" 
                    className="w-20 h-20 object-cover"
                  />
                ) : (
                  <div className="w-20 h-20 bg-secondary flex items-center justify-center">
                    <FileText size={32} className="text-muted-foreground" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{file.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {(file.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => { setFile(null); setFilePreview(null); }}
                >
                  <X size={16} />
                </Button>
              </div>
            </Card>
          )}
        </div>

        {/* Important Notice */}
        <Card className="p-4 bg-yellow-50 border-yellow-200 mb-8">
          <p className="text-sm text-yellow-800">
            <strong>Importante:</strong> Realize o pagamento pelo valor exato indicado acima. 
            Após enviar o comprovante, aguarde até <strong>48 horas</strong> para análise e 
            confirmação do pagamento. Seu acesso será liberado após a aprovação.
          </p>
        </Card>

        {/* Submit Button */}
        <Button 
          onClick={handleSubmit}
          disabled={!selectedMethod || !file || submitting}
          className="btn-primary w-full h-12"
          data-testid="submit-proof"
        >
          {submitting ? "Enviando..." : "Enviar comprovante"}
        </Button>

        {/* Validation Messages */}
        {!selectedMethod && (
          <p className="text-xs text-orange-600 text-center mt-2">
            Selecione um método de pagamento acima
          </p>
        )}

        {/* Terms */}
        <p className="text-xs text-muted-foreground text-center mt-4">
          Ao continuar, você concorda com os termos de uso e política de privacidade da AESTHA.
          Pagamento único, sem cobrança automática. Sem renovação automática.
        </p>
      </main>
    </div>
  );
}
